import React, { useMemo, memo } from 'react';

import classnames from 'classnames';

import { CALENDAR_VIEW_TYPES } from 'constants/calendar';

import { getDaysInMonth, getHighlightedDatesForMonth, isWeekend } from 'utils/calendar';
import { createDate } from 'utils/date';

import classes from './MonthCell.module.scss';

export const DayCircle = memo(({ isFuture, isWeekendDay, isPast, showSelectedDates, isTransparent }) => (
  <div
    className={classnames(classes.dayCircle, {
      [classes.dayCircleWeekEnd]: isWeekendDay && !isTransparent,
      [classes.dayCircleFuture]: isFuture && !isTransparent,
      [classes.dayCirclePast]: isPast && !isTransparent,
      [classes.dayCircleSelected]: isFuture && showSelectedDates && !isTransparent,
      [classes.dayCircleTransparent]: isTransparent,
    })}
  ></div>
));

export const DayBar = memo(({ isFuture, isWeekendDay, isPast, showSelectedDates, isTransparent }) => (
  <div
    className={classnames(classes.dayBar, {
      [classes.dayBarWeekEnd]: isWeekendDay && !isTransparent,
      [classes.dayBarFuture]: isFuture && !isTransparent,
      [classes.dayBarPast]: isPast && !isTransparent,
      [classes.dayBarSelected]: isFuture && showSelectedDates && !isTransparent,
      [classes.dayBarTransparent]: isTransparent,
    })}
  ></div>
));

// NOTE: Empty cell component for days before the month starts
const EmptyCell = memo(() => <div className={classes.emptyCell}></div>);

const MonthCell = memo(({ year, month, performedDates = [], productionId, viewType }) => {
  const totalDays = useMemo(() => getDaysInMonth(year, month + 1), [month, year]);
  const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays]);

  // NOTE: Get the first and last performed dates for transparency logic
  const performedDateRange = useMemo(() => {
    if (!performedDates || performedDates.length === 0) {
      return { firstDate: null, lastDate: null };
    }

    const sortedDates = [...performedDates]
      .map(item => item.startDate)
      .sort((a, b) => createDate(a).valueOf() - createDate(b).valueOf());

    return {
      firstDate: createDate(sortedDates[0]),
      lastDate: createDate(sortedDates[sortedDates.length - 1]),
    };
  }, [performedDates]);

  const highlightedDays = useMemo(() => {
    const result = getHighlightedDatesForMonth(performedDates, year, month);
    return result;
  }, [month, performedDates, year]);

  // NOTE: Helper function to check if a date should be transparent
  const isDateTransparent = date => {
    if (!performedDateRange.firstDate || !performedDateRange.lastDate) {
      return false;
    }

    return date.isBefore(performedDateRange.firstDate) || date.isAfter(performedDateRange.lastDate);
  };

  // NOTE: Calculate the first day of the month and determine what day of the week it falls on
  const firstDayOfMonth = useMemo(() => {
    const firstDate = createDate(`${year}-${month + 1}-1`);
    return firstDate.day(); // NOTE: Returns 0 for Sunday, 1 for Monday, etc.
  }, [year, month]);

  const renderedDays = useMemo(() => {
    const calendarCells = [];

    // NOTE: For quarter view, we don't need calendar grid layout
    if (viewType === CALENDAR_VIEW_TYPES.QUARTER) {
      return days?.map(day => {
        const date = createDate(`${year}-${month + 1}-${day}`);
        const isWeekendDay = isWeekend(date);
        const highlightedDay = highlightedDays?.find(d => d.day === day);
        const isHighlighted = !!highlightedDay;
        const isPast = highlightedDay?.isPast || false;
        const isTransparent = isDateTransparent(date);

        return (
          <DayBar
            key={`${productionId}-${year}-${month}-${day}`}
            isFuture={isHighlighted}
            isWeekendDay={isWeekendDay}
            isPast={isPast}
            isTransparent={isTransparent}
          />
        );
      });
    }

    // NOTE: Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i += 1) {
      calendarCells.push(<EmptyCell key={`empty-${i}`} />);
    }

    // NOTE: Add the actual days of the month
    days?.forEach(day => {
      const date = createDate(`${year}-${month + 1}-${day}`);
      const isWeekendDay = isWeekend(date);
      const highlightedDay = highlightedDays?.find(d => d.day === day);
      const isHighlighted = !!highlightedDay;
      const isPast = highlightedDay?.isPast || false;
      const isTransparent = isDateTransparent(date);

      calendarCells.push(
        <DayCircle
          key={`${productionId}-${year}-${month}-${day}`}
          isFuture={isHighlighted}
          isWeekendDay={isWeekendDay}
          isPast={isPast}
          isTransparent={isTransparent}
        />,
      );
    });

    return calendarCells;
  }, [days, year, month, highlightedDays, productionId, viewType, firstDayOfMonth]);

  return (
    <div
      className={classnames(classes.month, {
        [classes.monthsBar]: viewType === CALENDAR_VIEW_TYPES.QUARTER,
      })}
    >
      {renderedDays}
    </div>
  );
});

export default MonthCell;
